import produce from 'immer';
import { partition } from 'lodash/fp';

import { isIdCompareCheck } from 'dashboard/models/verification-check';
import { VerificationCheckConfig } from 'dashboard/models/verification-check-config/verification-check-config';
import {
  IdCompareOption,
  VerificationTemplateVersion,
  VerificationTemplateVersionSelfie,
} from 'dashboard/models/verification-template-version';
import { ApiModel } from 'lib/interfaces/api';
import { DeepPartial } from 'lib/types';

export const getIdCompareOption = (
  vtmplVersion: VerificationTemplateVersionSelfie,
): IdCompareOption | undefined => {
  return (vtmplVersion.attributes?.['verification-checks'] || []).find(isIdCompareCheck)?.[
    'id-compare-option'
  ];
};

const updateCheckConfigIdCompareOption = (
  vtmplVersion: VerificationTemplateVersionSelfie,
  idCompareOption: IdCompareOption,
): VerificationCheckConfig[] => {
  const verificationChecks = vtmplVersion?.attributes?.['verification-checks'] || [];
  if (vtmplVersion.type !== ApiModel.VerificationTemplateVersionSelfie) {
    return verificationChecks;
  }
  const [idCompareChecks, otherChecks] = partition(isIdCompareCheck, verificationChecks) as Array<
    Array<VerificationCheckConfig>
  >;
  const updatedIdCompareChecks = idCompareChecks.map((c) => {
    return {
      ...c,
      'id-compare-option': idCompareOption,
    };
  });
  return [...updatedIdCompareChecks, ...otherChecks];
};

export const produceVtmplVersionWithIdCompareOption = (
  vtmplVersion: VerificationTemplateVersion | DeepPartial<VerificationTemplateVersion>,
  idCompareOption: IdCompareOption,
): VerificationTemplateVersionSelfie | null => {
  if (vtmplVersion.type !== ApiModel.VerificationTemplateVersionSelfie) {
    return null;
  }

  const currentIdCompareOption = getIdCompareOption(
    vtmplVersion as VerificationTemplateVersionSelfie,
  );
  if (idCompareOption === currentIdCompareOption) {
    return null;
  }

  return produce(vtmplVersion, (draft) => {
    if (draft.attributes) {
      draft.attributes['verification-checks'] = updateCheckConfigIdCompareOption(
        vtmplVersion as VerificationTemplateVersionSelfie,
        idCompareOption,
      );
    }
  }) as VerificationTemplateVersionSelfie;
};
